/**
 * TemplateForm: renders a template's inputs from its field list (no per-template code).
 *
 *   const [values, setValues] = useState(() => initialValues(template));
 *   <TemplateForm inputs={template.inputs} values={values} onChange={setValues} errors={fieldErrors} />
 *   toApiInputs(template.inputs, values)   -> the body for POST /templates/:slug/runs
 *
 * text/textarea -> text box, number -> number box, select -> dropdown, boolean -> switch,
 * list -> one item per line (pasting a column from a spreadsheet works). Advanced fields sit in a collapsed section.
 * Pickers: date and dateRange -> calendar, month/week -> selects, year -> year list, country and language ->
 * searchable list with flags, airport -> type-ahead over the airport list (GET /templates/lookups/airports).
 * A select with many choices or storefront flags becomes searchable; a select with `dateRange` adds "Custom dates".
 * Inputs that take several values (`multiple`) are kept as one value per line in the form and sent as an array.
 *
 * Options: an input with `showIf` appears only while another input has the value it names (the Product Hunt week
 * field when Period is Week). Hidden fields slide closed, keep their value for when they come back, and are not sent.
 * templateFor(template, inputs) gives the columns and sample for the chosen options (variants, column showIf).
 */
import { ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import TextField, { TextArea } from "@/components/ui/TextField";
import NumberField from "@/components/ui/NumberField";
import Select from "@/components/ui/Select";
import Switch from "@/components/ui/Switch";
import { fieldClasses } from "@/components/ui/TextField";
import Combobox, { ComboOption } from "@/components/templates/pickers/Combobox";
import DatePicker, { addDays, resolveDate, todayIso } from "@/components/templates/pickers/DatePicker";
import { MonthPicker, WeekPicker, YearSelect } from "@/components/templates/pickers/PeriodPickers";
import { lookupAirports, searchAirports } from "@/services/templates.service";
import type { Airport, TemplateCondition, TemplateDetail, TemplateInput } from "@/services/templates.service";

export type FormValues = Record<string, string | number | boolean | null>;

/** Form values from a template's examples and defaults, or from saved inputs (a preset or a past run). */
export function initialValues(template: TemplateDetail, saved?: Record<string, unknown> | null): FormValues {
  const out: FormValues = {};
  for (const i of template.inputs) {
    const v = saved && saved[i.key] !== undefined ? saved[i.key] : i.example !== undefined ? i.example : i.default;
    if (i.type === "list") out[i.key] = Array.isArray(v) ? v.join("\n") : typeof v === "string" ? v : "";
    else if (i.multiple) out[i.key] = (Array.isArray(v) ? v.map(String) : typeof v === "string" ? v.split(/\r?\n|,/) : []).map(x => x.trim()).filter(Boolean).join("\n");
    else if (i.type === "year") out[i.key] = v != null && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null;
    else if (i.type === "boolean") out[i.key] = typeof v === "boolean" ? v : false;
    else if (i.type === "number") out[i.key] = typeof v === "number" ? v : v != null && v !== "" ? Number(v) : null;
    else out[i.key] = v == null ? "" : String(v);
  }
  return out;
}

const isEmpty = (v: unknown) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/** Whether a condition holds for these values (form values or a run's inputs). Same rules as the API's conditionMet. */
export function conditionMet(cond: TemplateCondition | undefined, values: Record<string, unknown>): boolean {
  if (!cond) return true;
  const v = values[cond.input];
  const same = (x: unknown) => !isEmpty(v) && String(v) === String(x);
  if (cond.equals !== undefined) return same(cond.equals);
  if (cond.in) return cond.in.some(same);
  return !isEmpty(v) && v !== false && v !== 0;
}

/** Values with each input's default where the value is empty, as the API reads them. */
function withDefaults(inputs: TemplateInput[], values: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...values };
  for (const i of inputs) if (isEmpty(out[i.key]) && i.default !== undefined) out[i.key] = i.default;
  return out;
}

/** Keys of the inputs that show with these values. A field hidden by its own condition counts as empty for later ones. */
export function visibleInputs(inputs: TemplateInput[], values: Record<string, unknown>): Set<string> {
  const all = withDefaults(inputs, values);
  const seen: Record<string, unknown> = {};
  const shown = new Set<string>();
  for (const i of inputs) {
    if (i.showIf && !conditionMet(i.showIf, seen)) continue;
    shown.add(i.key);
    seen[i.key] = all[i.key];
  }
  return shown;
}

/**
 * The template's columns and sample for these inputs: matching variants replace them, columns with `showIf` are
 * kept only when it holds. `variantKey` names the options, so a column choice is remembered per option.
 */
export function templateFor(template: TemplateDetail, inputs: Record<string, unknown>): TemplateDetail {
  if (!template.variants?.length && !template.columns.some(c => c.showIf)) return template;
  // As the API reads them: defaults for empty fields, nothing for hidden ones (a hidden field's default never counts).
  const shown = visibleInputs(template.inputs, inputs);
  const values = Object.fromEntries(Object.entries(withDefaults(template.inputs, inputs)).filter(([k]) => shown.has(k)));
  let columns = template.columns;
  let sample = template.sample;
  const key: string[] = [];
  (template.variants ?? []).forEach((v, n) => {
    if (!conditionMet(v.when, values)) return;
    key.push(`v${n}`);
    if (v.columns) columns = v.columns;
    if (v.sample) sample = v.sample;
  });
  const conditional = columns.filter(c => c.showIf && conditionMet(c.showIf, values));
  if (conditional.length) key.push(`c${conditional.length}`);
  return { ...template, columns: columns.filter(c => conditionMet(c.showIf, values)), sample, variantKey: key.join(".") || undefined };
}

/** Form values to API inputs: lists become arrays, empty values are left out so defaults apply, hidden fields are not sent. */
export function toApiInputs(inputs: TemplateInput[], values: FormValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const shown = visibleInputs(inputs, values);
  for (const i of inputs) {
    if (!shown.has(i.key)) continue;
    const v = values[i.key];
    if (i.type === "list" || i.multiple) {
      const items = String(v ?? "")
        .split(/\r?\n/)
        .map(s => s.trim())
        .filter(Boolean);
      if (items.length) out[i.key] = items;
    } else if (i.type === "boolean") {
      out[i.key] = !!v;
    } else if (i.type === "number" || i.type === "year") {
      if (v !== null && v !== "" && v !== undefined) out[i.key] = Number(v);
    } else if (i.type === "select" || ((i.type === "country" || i.type === "language") && i.options?.some(o => o.value === v))) {
      // An option can be "" (for example "Any time"), so selects are always sent.
      out[i.key] = v ?? "";
    } else if (v !== "" && v != null) {
      out[i.key] = v;
    }
  }
  return out;
}

function Label({ input, htmlFor }: { input: TemplateInput; htmlFor: string }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-fg">
      {input.label}
      {input.required && (
        <span className="text-danger-text" aria-hidden="true">
          {" "}
          *
        </span>
      )}
    </label>
  );
}

const lines = (v: FormValues[string]) =>
  String(v ?? "")
    .split(/\r?\n/)
    .map(s => s.trim())
    .filter(Boolean);

const EXTRA_ICON = "lucide:globe";

/** Country and language choices for the Combobox: flags for countries, the code on the right. */
function pickerOptions(input: TemplateInput): ComboOption[] {
  return (input.options ?? []).map(o => {
    if (input.type === "country") {
      const isCode = /^[a-z]{2}$/i.test(o.value);
      return { value: o.value, label: o.label, keywords: o.keywords, hint: isCode ? o.value.toUpperCase() : undefined, flag: isCode ? o.value : undefined, icon: isCode ? undefined : EXTRA_ICON };
    }
    if (input.type === "language") return { value: o.value, label: o.label, hint: o.value };
    return { value: o.value, label: o.label, keywords: o.keywords, flag: o.country };
  });
}

const airportOption = (a: Airport): ComboOption => ({
  value: a.code,
  label: a.kind === "city" ? `${a.city}, all airports` : a.name,
  short: a.city,
  hint: a.code,
  detail: a.kind === "city" ? `${(a.airports ?? []).join(", ")} · ${a.countryName}` : `${a.city}, ${a.countryName}`,
  flag: a.country,
  keywords: a.city,
});

/** Airports: type-ahead from the API; labels for codes already in the form are looked up once. */
function AirportField({ id, input, value, onChange, invalid, describedBy }: { id: string; input: TemplateInput; value: FormValues[string]; onChange: (v: string) => void; invalid: boolean; describedBy?: string }) {
  const codes = lines(value);
  const [known, setKnown] = useState<Record<string, ComboOption>>({});
  const missing = codes.filter(c => /^[A-Z]{3}$/.test(c) && !known[c]).join(",");
  useEffect(() => {
    if (!missing) return;
    let cancelled = false;
    lookupAirports(missing.split(","))
      .then(list => !cancelled && setKnown(k => ({ ...k, ...Object.fromEntries(list.map(a => [a.code, airportOption(a)])) })))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [missing]);
  const load = useCallback((q: string) => searchAirports(q).then(r => r.map(airportOption)), []);
  return (
    <Combobox
      id={id}
      multiple={input.multiple}
      maxItems={input.maxItems ?? (input.multiple ? undefined : 1)}
      value={codes}
      onChange={v => onChange(v.join("\n"))}
      load={load}
      known={known}
      allowCustom={q => (q.length >= 3 && !/^[a-z]{3}$/i.test(q) ? q : null)}
      customLabel={v => `Search "${v}" by name`}
      placeholder={input.placeholder ?? "City or airport"}
      invalid={invalid}
      describedBy={describedBy}
      required={input.required}
      emptyText="No airport or city matches"
    />
  );
}

/** A select with preset ranges plus "Custom dates" (Google Trends' time range). */
const RANGE = /^\d{4}-\d{2}-\d{2} \d{4}-\d{2}-\d{2}$/;
function SelectWithRange({ id, input, value, onChange, invalid, describedBy }: { id: string; input: TemplateInput; value: string; onChange: (v: string) => void; invalid: boolean; describedBy?: string }) {
  const isRange = RANGE.test(value);
  const today = todayIso();
  const bounds = input.dateRange ?? {};
  return (
    <div className="space-y-2">
      <Select
        aria-label={input.label}
        className={`${fieldClasses(invalid)} h-9 px-3 text-base sm:text-sm`}
        value={isRange ? "__custom" : value}
        onChange={v => onChange(v === "__custom" ? `${addDays(today, -364)} ${today}` : v)}
        options={[...(input.options ?? []).map(o => ({ value: o.value, label: o.label })), { value: "__custom", label: "Custom dates" }]}
      />
      {isRange && (
        <DatePicker
          id={id}
          range
          required
          value={value}
          onChange={onChange}
          min={resolveDate(bounds.minDate)}
          max={resolveDate(bounds.maxDate)}
          maxDays={bounds.maxDays}
          invalid={invalid}
          describedBy={describedBy}
        />
      )}
    </div>
  );
}

function Field({
  input,
  value,
  onChange,
  error,
  values,
}: {
  input: TemplateInput;
  value: FormValues[string];
  onChange: (v: FormValues[string]) => void;
  error?: string;
  values: FormValues;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [input.help ? hintId : null, error ? errId : null].filter(Boolean).join(" ") || undefined;
  const hint = input.help ? (
    <p id={hintId} className="mt-1.5 text-xs leading-5 text-fg-muted">
      {input.help}
    </p>
  ) : null;
  const err = error ? (
    <p id={errId} className="mt-1.5 flex items-start gap-1 text-xs text-danger-text" role="alert">
      <Icon icon="lucide:circle-alert" className="mt-0.5 shrink-0 text-xs" aria-hidden="true" />
      {error}
    </p>
  ) : null;

  switch (input.type) {
    case "text":
      return (
        <TextField
          label={input.label}
          required={input.required}
          value={String(value ?? "")}
          placeholder={input.placeholder}
          hint={input.help}
          error={error}
          onChange={e => onChange(e.target.value)}
        />
      );
    case "textarea":
      return (
        <TextArea
          label={input.label}
          required={input.required}
          rows={3}
          value={String(value ?? "")}
          placeholder={input.placeholder}
          hint={input.help}
          error={error}
          onChange={e => onChange(e.target.value)}
        />
      );
    case "list": {
      const count = String(value ?? "")
        .split(/\r?\n/)
        .filter(s => s.trim()).length;
      return (
        <div>
          <div className="flex items-baseline justify-between gap-3">
            <Label input={input} htmlFor={id} />
            {input.maxItems ? (
              <span className={`text-xs tabular-nums ${count > input.maxItems ? "text-danger-text" : "text-fg-muted"}`}>
                {count} of {input.maxItems}
              </span>
            ) : null}
          </div>
          <textarea
            id={id}
            rows={Math.min(8, Math.max(3, count + 1))}
            value={String(value ?? "")}
            placeholder={input.placeholder ?? "One per line"}
            aria-invalid={!!error || undefined}
            aria-describedby={describedBy}
            onChange={e => onChange(e.target.value)}
            spellCheck={false}
            className={`${fieldClasses(!!error)} resize-y px-3 py-2 font-mono text-13 leading-6`}
          />
          {err ?? hint}
        </div>
      );
    }
    case "number":
      if (input.options?.length) {
        const opts: ComboOption[] = input.options.map(o => ({ value: o.value, label: o.label, hint: o.value }));
        const v = value == null || value === "" ? "" : String(value);
        return (
          <div>
            <Label input={input} htmlFor={id} />
            <Combobox
              id={id}
              value={[v]}
              known={v && !opts.some(o => o.value === v) ? { [v]: { value: v, label: `Category ${v}`, hint: v } } : undefined}
              onChange={x => onChange(x[0] === undefined || x[0] === "" ? null : Number(x[0]))}
              options={opts}
              allowCustom={q => (/^\d{1,6}$/.test(q) ? q : null)}
              customLabel={x => `Use number ${x}`}
              invalid={!!error}
              describedBy={describedBy}
            />
            {err ?? hint}
          </div>
        );
      }
      return (
        <div>
          <Label input={input} htmlFor={id} />
          <NumberField
            id={id}
            value={typeof value === "number" ? value : value == null || value === "" ? null : Number(value)}
            onChange={v => onChange(v)}
            min={input.min}
            max={input.max}
            allowEmpty={!input.required}
            aria-invalid={!!error || undefined}
            aria-describedby={describedBy}
            className={`${fieldClasses(!!error)} h-9 px-3 text-sm`}
          />
          {err ?? hint}
        </div>
      );
    case "select": {
      const opts = input.options ?? [];
      if (input.dateRange) {
        return (
          <div>
            <Label input={input} htmlFor={id} />
            <SelectWithRange id={id} input={input} value={String(value ?? "")} onChange={onChange} invalid={!!error} describedBy={describedBy} />
            {err ?? hint}
          </div>
        );
      }
      // Long lists and storefronts (with flags) are searchable.
      if (opts.length > 12 || opts.some(o => o.country)) {
        return (
          <div>
            <Label input={input} htmlFor={id} />
            <Combobox id={id} value={[String(value ?? "")]} onChange={v => onChange(v[0] ?? "")} options={pickerOptions(input)} invalid={!!error} describedBy={describedBy} required={input.required} />
            {err ?? hint}
          </div>
        );
      }
      return (
        <div>
          <span id={id} className="mb-1.5 block text-sm font-medium text-fg">
            {input.label}
          </span>
          <Select
            aria-label={input.label}
            className={`${fieldClasses(!!error)} h-9 px-3 text-base sm:text-sm`}
            value={String(value ?? "")}
            onChange={v => onChange(v)}
            options={opts.map(o => ({ value: o.value, label: o.label }))}
          />
          {err ?? hint}
        </div>
      );
    }
    case "country":
    case "language": {
      const sub = input.type === "country" && input.subdivisions;
      return (
        <div>
          <Label input={input} htmlFor={id} />
          <Combobox
            id={id}
            multiple={input.multiple}
            maxItems={input.maxItems}
            value={input.multiple ? lines(value) : [String(value ?? "")]}
            onChange={v => onChange(input.multiple ? v.join("\n") : (v[0] ?? ""))}
            options={pickerOptions(input)}
            allowCustom={sub ? q => (/^[a-z]{2}-[a-z0-9]{1,3}$/i.test(q) ? q.toUpperCase() : null) : undefined}
            customLabel={v => `Use region ${v}`}
            placeholder={input.type === "country" ? "Search countries" : "Search languages"}
            invalid={!!error}
            describedBy={describedBy}
            required={input.required}
          />
          {err ?? hint}
        </div>
      );
    }
    case "airport":
      return (
        <div>
          <div className="flex items-baseline justify-between gap-3">
            <Label input={input} htmlFor={id} />
            {input.multiple && input.maxItems && input.maxItems > 3 ? (
              <span className="text-xs tabular-nums text-fg-muted">
                {lines(value).length} of {input.maxItems}
              </span>
            ) : null}
          </div>
          <AirportField id={id} input={input} value={value} onChange={onChange} invalid={!!error} describedBy={describedBy} />
          {err ?? hint}
        </div>
      );
    case "date":
    case "dateRange": {
      let min = resolveDate(input.minDate);
      const other = input.notBefore ? String(values[input.notBefore] ?? "") : "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(other) && (!min || other > min)) min = other;
      const span = input.spanDays ? Number(values[input.spanDays]) || undefined : undefined;
      return (
        <div>
          <Label input={input} htmlFor={id} />
          <DatePicker
            id={id}
            range={input.type === "dateRange"}
            value={String(value ?? "")}
            onChange={v => onChange(v)}
            min={min}
            max={resolveDate(input.maxDate)}
            maxDays={input.maxDays}
            span={span}
            required={input.required}
            invalid={!!error}
            describedBy={describedBy}
            placeholder={input.required ? undefined : input.type === "date" && input.notBefore ? "No return (one way)" : "Any date"}
          />
          {err ?? hint}
        </div>
      );
    }
    case "month":
    case "week": {
      const Picker = input.type === "month" ? MonthPicker : WeekPicker;
      return (
        <div>
          <Label input={input} htmlFor={id} />
          <Picker id={id} label={input.label} value={String(value ?? "")} onChange={v => onChange(v)} min={resolveDate(input.minDate)} max={resolveDate(input.maxDate)} invalid={!!error} />
          {err ?? hint}
        </div>
      );
    }
    case "year": {
      const max = input.max ?? new Date().getFullYear();
      const min = input.min ?? 1900;
      if (!input.multiple && max - min <= 20) {
        return (
          <div>
            <Label input={input} htmlFor={id} />
            <YearSelect id={id} label={input.label} value={typeof value === "number" ? value : null} onChange={v => onChange(v)} min={min} max={max} optional={!input.required} invalid={!!error} />
            {err ?? hint}
          </div>
        );
      }
      const years: ComboOption[] = Array.from({ length: max - min + 1 }, (_, i) => String(max - i)).map(y => ({ value: y, label: y }));
      return (
        <div>
          <Label input={input} htmlFor={id} />
          <Combobox
            id={id}
            multiple={input.multiple}
            maxItems={input.maxItems}
            value={input.multiple ? lines(value) : value == null || value === "" ? [] : [String(value)]}
            onChange={v => onChange(input.multiple ? v.join("\n") : v[0] ? Number(v[0]) : null)}
            options={input.multiple || input.required ? years : [{ value: "", label: "Any year" }, ...years]}
            placeholder={input.multiple ? "Add years" : "Any year"}
            invalid={!!error}
            describedBy={describedBy}
            required={input.required}
          />
          {err ?? hint}
        </div>
      );
    }
    case "boolean":
      return (
        <div>
          <Switch checked={!!value} onCheckedChange={v => onChange(v)} label={input.label} description={input.help} />
          {err}
        </div>
      );
  }
}

/**
 * A field that slides open or closed when its condition changes (height and fade, about 200ms; none with reduced
 * motion). Closed fields are unmounted once the animation ends; their value stays in the form state. Spacing lives
 * inside (pt-5), so a closing field takes its gap with it and nothing jumps.
 */
const REVEAL_MS = 200;
function Reveal({ show, children }: { show: boolean; children: ReactNode }) {
  const [mounted, setMounted] = useState(show);
  const [open, setOpen] = useState(show);
  const [settled, setSettled] = useState(true);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setSettled(false);
    if (show) {
      setMounted(true);
      // Paint closed first, then open, so the height animates.
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setOpen(true)));
      const done = setTimeout(() => setSettled(true), REVEAL_MS + 20);
      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(done);
      };
    }
    setOpen(false);
    const done = setTimeout(() => {
      setMounted(false);
      setSettled(true);
    }, REVEAL_MS + 20);
    return () => clearTimeout(done);
  }, [show]);
  if (!mounted) return null;
  return (
    <div
      inert={!show}
      aria-hidden={!show || undefined}
      className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
    >
      {/* Clipped only while moving, so focus rings and open pickers are not cut off. */}
      <div className={`min-h-0 ${open && settled ? "" : "overflow-hidden"}`}>
        <div className="pt-5">{children}</div>
      </div>
    </div>
  );
}

export default function TemplateForm({
  inputs,
  values,
  onChange,
  errors = {},
}: {
  inputs: TemplateInput[];
  values: FormValues;
  onChange: (next: FormValues) => void;
  errors?: Record<string, string>;
}) {
  const shown = visibleInputs(inputs, values);
  const basic = inputs.filter(i => !i.advanced);
  const advanced = inputs.filter(i => i.advanced);
  const advancedHasError = advanced.some(i => shown.has(i.key) && errors[i.key]);
  const [open, setOpen] = useState(false);
  const showAdvanced = open || advancedHasError;
  const set = (key: string) => (v: FormValues[string]) => onChange({ ...values, [key]: v });
  const field = (i: TemplateInput) => (
    <Reveal key={i.key} show={shown.has(i.key)}>
      <Field input={i} value={values[i.key]} values={values} onChange={set(i.key)} error={shown.has(i.key) ? errors[i.key] : undefined} />
    </Reveal>
  );

  return (
    <div>
      {/* Each field carries its own top gap (Reveal), so the first one is pulled up by the same amount. */}
      <div className="-mt-5">{basic.map(field)}</div>
      {advanced.some(i => shown.has(i.key)) && (
        <div className="mt-5 border-t border-line pt-4">
          <button
            type="button"
            aria-expanded={showAdvanced}
            onClick={() => setOpen(o => !o)}
            className="flex w-full items-center justify-between rounded-control py-1 text-sm font-medium text-fg-secondary outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-primary"
          >
            Advanced
            <Icon icon="lucide:chevron-down" className={`text-base transition-transform duration-150 ${showAdvanced ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
          {showAdvanced && <div className="-mt-1">{advanced.map(field)}</div>}
        </div>
      )}
    </div>
  );
}
